import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LockSimple, Check, UserGear, ArrowSquareOut } from "@phosphor-icons/react";

const PERM_META = [
  ["read", "Read", "View SOPs, tasks, handovers and briefings"],
  ["write", "Write", "Complete tasks, submit checks, log handovers"],
  ["approve", "Approve", "Approve staff SOP drafts and swap requests"],
  ["publish", "Publish", "Publish SOPs, videos and vault content"],
  ["broadcast", "Broadcast", "Broadcast alerts and directives"],
  ["roster_edit", "Roster Edit", "Create and edit the duty roster"],
];

const ROLE_COLS = [
  { value: "admin", label: "Master Admin", locked: true },
  { value: "gm", label: "General Manager", locked: false },
  { value: "manager", label: "Dept Manager", locked: false },
  { value: "supervisor", label: "Supervisor", locked: false },
  { value: "staff", label: "Line Staff", locked: false },
];

function Toggle({ on, onClick, testId, disabled }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"} ${disabled ? "opacity-40" : "tap-press"}`}>
      <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-6" : "left-1"}`} />
    </button>
  );
}

export default function PermissionsPage() {
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [effective, setEffective] = useState({});
  const [dirty, setDirty] = useState(false);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/permissions/matrix").then((r) => {
      setMatrix(r.data);
      setEffective(JSON.parse(JSON.stringify(r.data.effective)));
      setDirty(false);
    }).catch((e) => toast.error(apiError(e)));
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const flip = (role, key) => {
    setEffective((prev) => ({ ...prev, [role]: { ...prev[role], [key]: !prev[role]?.[key] } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/permissions/matrix", { role_perms: effective });
      toast.success("Role permissions updated — applies on each member's next request");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleDelegation = async (member) => {
    try {
      await api.put(`/team/${member.id}`, { can_allocate_handover_tasks: !member.can_allocate_handover_tasks });
      setTeam((prev) => prev.map((m) => m.id === member.id ? { ...m, can_allocate_handover_tasks: !member.can_allocate_handover_tasks } : m));
      toast.success(`${member.name} ${member.can_allocate_handover_tasks ? "can no longer" : "can now"} allocate handover tasks`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const lineTeam = team.filter((m) => ["staff", "supervisor"].includes(m.role));
  const operators = team.filter((m) => m.is_master_operator);
  const gmCantEdit = (role) => user.role === "gm" && role === "admin";

  if (!matrix) return <p className="text-slate-400">Loading permissions…</p>;

  return (
    <div className="space-y-8 rise-in" data-testid="permissions-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Access Control</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <ShieldCheck size={34} weight="duotone" className="text-emerald-500" /> Permissions
          </h1>
        </div>
        {dirty && (
          <button data-testid="permissions-save-button" onClick={save} disabled={busy}
            className="h-12 px-7 rounded-full bg-slate-900 text-white text-sm font-bold tap-press hover:bg-slate-800 disabled:opacity-50 shrink-0">
            {busy ? "Saving…" : "Save changes"}
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="role-permission-matrix">
        <h2 className="font-heading text-xl font-bold mb-1">Role permission matrix</h2>
        <p className="text-sm text-slate-500 mb-6">Brand-level defaults per role. Per-member overrides live in each member's Access control panel under <Link to="/venues" className="font-bold underline">Venues &amp; Team</Link>. Master Admin authority is locked at full access.</p>
        <div className="overflow-x-auto">
          <table className="w-full border-separate" style={{ borderSpacing: "0 8px" }}>
            <thead>
              <tr>
                <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-wider pr-4 min-w-52">Permission</th>
                {ROLE_COLS.map((r) => (
                  <th key={r.value} className="text-xs font-bold text-slate-500 px-3 pb-1 min-w-28">
                    <span className="inline-flex items-center gap-1">{r.locked && <LockSimple size={12} />}{r.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERM_META.map(([key, label, desc]) => (
                <tr key={key} data-testid={`perm-row-${key}`}>
                  <td className="pr-4 py-2">
                    <p className="text-sm font-bold">{label}</p>
                    <p className="text-[11px] text-slate-400">{desc}</p>
                  </td>
                  {ROLE_COLS.map((r) => (
                    <td key={r.value} className="px-3 text-center">
                      {r.locked ? (
                        <span data-testid={`perm-locked-admin-${key}`} className="inline-flex h-7 w-12 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600">
                          <Check size={16} weight="bold" />
                        </span>
                      ) : (
                        <Toggle on={!!effective[r.value]?.[key]} disabled={gmCantEdit(r.value)}
                          onClick={() => flip(r.value, key)} testId={`perm-matrix-${r.value}-${key}`} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 mt-4">Master Operators and the Master Admin bypass this matrix. Changes apply on each member's next request.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="delegated-access-panel">
          <div className="flex items-center gap-3 mb-1">
            <UserGear size={24} weight="duotone" className="text-sky-500" />
            <h2 className="font-heading text-xl font-bold">Delegated access</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Allow trusted line staff to allocate tasks and assignees inside shift handovers.</p>
          <div className="space-y-2">
            {lineTeam.map((m) => (
              <div key={m.id} data-testid={`delegation-row-${m.id}`} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{m.name}</p>
                  <p className="text-[11px] text-slate-400">{m.position || m.role}</p>
                </div>
                <Toggle on={!!m.can_allocate_handover_tasks} onClick={() => toggleDelegation(m)} testId={`delegation-toggle-${m.id}`} />
              </div>
            ))}
            {lineTeam.length === 0 && <p className="text-sm text-slate-400">No line staff yet.</p>}
          </div>
        </div>

        <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8" data-testid="master-operators-panel">
          <h2 className="font-heading text-xl font-bold mb-1">Master Operators</h2>
          <p className="text-sm text-slate-400 mb-5">Members granted full administrative bypass by the Master Admin.</p>
          <div className="space-y-2">
            {operators.map((m) => (
              <div key={m.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="text-sm font-bold">{m.name}</p>
                  <p className="text-[11px] text-slate-400">{m.position || m.role}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold bg-violet-500/20 text-violet-300 border border-violet-400/30 px-2.5 py-1 rounded-full">Operator</span>
              </div>
            ))}
            {operators.length === 0 && <p className="text-sm text-slate-500">None delegated.</p>}
          </div>
          <Link to="/venues" data-testid="permissions-venues-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-emerald-400 hover:text-emerald-300">
            Manage per-member access in Venues &amp; Team <ArrowSquareOut size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
